import React from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setStep,
  setIsCreating,
  setBreadcrumbs,
} from "../../store/slices/navigationSlice";
import { useServices } from "../../context/ServiceProvider";
import { Card, List, Input, Spin, Empty, Typography, Button } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { getStepComponent } from "../../config/steps";
import RecentsPanel from "./RecentsPanel";
import { useRecents } from "../../hooks/useRecents";
import CloneProjectModal from "./CloneProjectModal";
import { Plus } from "lucide-react";
import {
  validateStep1,
  validateStep2,
  validateStep3,
  validateStep4,
} from "../../utils/stepValidation";

const WizardSteps = () => {
  const dispatch = useDispatch();
  const currentStep = useSelector((state) => state.navigation.currentStep);
  const isCreating = useSelector((state) => state.navigation.isCreating);
  const showValidationErrors = useSelector(
    (state) => state.navigation.showValidationErrors,
  );
  const { project } = useServices();
  const navigate = useNavigate();
  const [search, setSearch] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [showCloneModal, setShowCloneModal] = React.useState(false);
  const debounceRef = React.useRef(null);
  const {
    items: recentItems,
    remove: removeRecent,
    clear: clearRecents,
    record,
    touch,
  } = useRecents({
    storageKey: "RECENTS::v1",
    maxItems: 50,
  });

  // Validation for current step - select from creation state
  const metadata =
    useSelector((state) => state.project.creation.metadata) || {};
  const objectives =
    useSelector((state) => state.projectObjective.creation.objectives) || [];
  const qualificationSteps =
    useSelector(
      (state) => state.qualificationStep.creation.qualificationSteps,
    ) || [];
  const pages = useSelector((state) => state.page.creation.pages) || [];

  const getCurrentValidation = () => {
    // Default: valid for steps without validation or non-creating mode
    if (!isCreating) return { isValid: true, errors: [] };

    if (currentStep === 1) return validateStep1(metadata);
    if (currentStep === 2) {
      console.log("🔍 WizardSteps - Validating Step 2");
      console.log("🔍 WizardSteps - Objectives array:", objectives);
      console.log("🔍 WizardSteps - Objectives count:", objectives?.length);

      // Manual check for workType before calling validation
      if (objectives && objectives.length > 0) {
        objectives.forEach((obj, idx) => {
          console.log(`🔍 Objective ${idx + 1}:`, {
            id: obj.id,
            objectiveName: obj.objectiveName,
            workType: obj.workType,
            workTypeType: typeof obj.workType,
            hasWorkType: obj.hasOwnProperty("workType"),
            workTypeIsEmpty:
              obj.workType === undefined ||
              obj.workType === null ||
              obj.workType === "" ||
              (typeof obj.workType === "string" && obj.workType.trim() === ""),
          });
        });
      }

      const validation = validateStep2(objectives);
      console.log("🔍 WizardSteps - Step 2 validation result:", validation);
      console.log("🔍 WizardSteps - Validation isValid:", validation.isValid);
      console.log("🔍 WizardSteps - Validation errors:", validation.errors);
      return validation;
    }
    if (currentStep === 3) return validateStep3(qualificationSteps, metadata);
    if (currentStep === 4) return validateStep4(pages);
    // Steps 5 and 6 have no validation - always valid
    return { isValid: true, errors: [] };
  };

  const currentValidation = getCurrentValidation();

  console.log("🔍 WizardSteps - shouldShowErrors calculation:", {
    currentStep,
    showValidationErrors,
    hasObjectives: objectives && objectives.length > 0,
    isValid: currentValidation.isValid,
    errorsCount: currentValidation.errors?.length,
    errors: currentValidation.errors,
  });

  // Show errors if:
  // 1. User attempted to proceed (showValidationErrors is true) OR
  // 2. We're on step 2 or 3 and there are validation errors (show immediately for existing items with issues)
  // This ensures errors are visible even before clicking Next for existing objectives/qual steps
  const shouldShowErrors =
    (showValidationErrors ||
      (currentStep === 2 && objectives && objectives.length > 0) ||
      (currentStep === 3 &&
        qualificationSteps &&
        qualificationSteps.length > 0)) &&
    !currentValidation.isValid &&
    currentValidation.errors &&
    currentValidation.errors.length > 0;

  console.log("🔍 WizardSteps - shouldShowErrors:", shouldShowErrors);

  React.useEffect(() => {
    const term = (search || "").trim();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (term.length < 3) {
      setSearchLoading(false);
      setResults([]);
      return;
    }
    setSearchLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await project.searchProjects(term);
        setResults(Array.isArray(r) ? r : []);
        setSearchOpen(true);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, project]);

  const renderStep = () => {
    if (!isCreating) {
      return (
        <div className="flex-1 flex flex-col h-full bg-gradient-to-br from-gray-50 to-gray-100">
          {/* Welcome Section - Fixed at top */}
          <div className="px-8 pt-6 pb-4 bg-white border-b border-gray-200 shadow-sm">
            <h1 className="text-3xl font-bold text-gray-800 mb-1">
              Welcome to CrowdGen Delivery
            </h1>
            <p className="text-gray-600 text-sm">
              Manage your projects or create a new one to get started
            </p>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-hidden max-h-[calc(100% - 107px)] m-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-[1600px] h-full overflow-hidden">
              {/* Recently Viewed - Takes 2 columns */}
              <div className="lg:col-span-2 h-full overflow-hidden">
                <Card
                  title={
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-teal-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-base font-semibold text-gray-800">
                        Recently Viewed Projects
                      </span>
                    </div>
                  }
                  className="rounded-lg border border-gray-200"
                  style={{
                    height: "100%",
                    overflow: "hidden",
                  }}
                  styles={{
                    body: {
                      overflow: "hidden",
                      height: "calc(100% - 56px)",
                      padding: 0,
                    },
                  }}
                  extra={
                    <div className="relative w-64">
                      <Input.Search
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search projects..."
                        allowClear
                        onFocus={() => setSearchOpen(true)}
                        onBlur={() => setSearchOpen(false)}
                      />
                      {searchOpen && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
                          {searchLoading ? (
                            <div className="p-3 text-sm text-gray-500">
                              <Spin size="small" /> Searching…
                            </div>
                          ) : search.length < 3 ? (
                            <div className="p-3 text-sm text-gray-500">
                              <div>Enter at least 3 characters to search. </div>
                            </div>
                          ) : results.length === 0 ? (
                            <div className="p-3 text-sm text-gray-500">
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="No matches"
                              />
                            </div>
                          ) : (
                            <List
                              itemLayout="horizontal"
                              dataSource={results}
                              renderItem={(p) => (
                                <List.Item className="px-3 py-2 hover:bg-gray-50">
                                  <div
                                    role="button"
                                    className="w-full text-left"
                                    style={{
                                      cursor: "pointer",
                                      paddingLeft: 16,
                                    }}
                                    onMouseDown={() => {
                                      setSearch("");
                                      setResults([]);
                                      setSearchOpen(false);
                                      const id = p.Id || p.id;
                                      const name =
                                        (p && p.metadata && p.metadata.name) ||
                                        p.Name ||
                                        p.name;
                                      if (id && name) {
                                        record({
                                          id: String(id),
                                          title: String(name),
                                          url: `/p/${id}`,
                                          type: "Project",
                                        });
                                      }
                                      // Ensure breadcrumb trail starts from Home when navigating from Home search
                                      dispatch(
                                        setBreadcrumbs([
                                          {
                                            label: "Home",
                                            path: "/",
                                            type: "home",
                                          },
                                        ]),
                                      );
                                      navigate(`/p/${id || ""}`, {
                                        state: { crumbLabel: name },
                                      });
                                    }}
                                  >
                                    <div className="text-gray-900 text-sm font-medium">
                                      {p.Name || p.name}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {p.Id || p.id}
                                    </div>
                                  </div>
                                </List.Item>
                              )}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  }
                >
                  <div className="h-full overflow-hidden">
                    <RecentsPanel
                      items={recentItems}
                      onRemove={removeRecent}
                      onClear={clearRecents}
                      onItemClick={(item) => {
                        // Update last accessed timestamp when opening from recents
                        touch(item.id);
                        if (item.url) {
                          record({
                            id: String(item.id),
                            title: String(item.title),
                            url: item.url,
                            type: item.type,
                          });
                          navigate(item.url, {
                            state: { crumbLabel: item.title },
                          });
                        } else {
                          record({
                            id: String(item.id),
                            title: String(item.title),
                            url: `/p/${item.id}`,
                            type: "Project",
                          });
                          navigate(`/p/${item.id}`, {
                            state: { crumbLabel: item.title },
                          });
                        }
                      }}
                    />
                  </div>
                </Card>
              </div>

              {/* Quick Actions - Takes 1 column */}
              <div className="space-y-5 overflow-auto">
                {/* Create New Project Card */}
                <Card
                  className="shadow-md rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                  bodyStyle={{ padding: 0 }}
                >
                  <div className="bg-gradient-to-br from-teal-500 to-teal-600 p-6 text-white">
                    <div className="flex items-center gap-2 mb-3">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      <h3 className="text-lg font-semibold">
                        Create New Project
                      </h3>
                    </div>
                    <p className="text-teal-50 text-sm mb-5 leading-relaxed">
                      Start a fresh project from scratch with our guided setup
                      wizard
                    </p>
                    <Link
                      to="/create"
                      onClick={() => {
                        dispatch(setIsCreating(true));
                        dispatch(setStep(1));
                      }}
                      className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-white text-teal-600 rounded-lg hover:bg-teal-50 transition-all font-medium shadow-sm hover:shadow"
                    >
                      Get Started
                    </Link>
                  </div>
                </Card>

                {/* Clone Project Card */}
                <Card
                  className="shadow-md rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                  bodyStyle={{ padding: 0 }}
                >
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white">
                    <div className="flex items-center gap-2 mb-3">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      <h3 className="text-lg font-semibold">
                        Clone Existing Project
                      </h3>
                    </div>
                    <p className="text-blue-50 text-sm mb-5 leading-relaxed">
                      Copy settings from an existing project to save time
                    </p>
                    <button
                      onClick={() => setShowCloneModal(true)}
                      className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all font-medium shadow-sm hover:shadow"
                    >
                      Clone Project
                    </button>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="flex-1 flex flex-col min-h-0">
        {/* Validation Error Banner at Top - Shows when there are validation errors */}
        {shouldShowErrors && (
          <div className="px-6 py-3 bg-red-50 border-b border-red-200">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 flex-shrink-0 text-red-600 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-red-800 mb-1.5">
                  {currentValidation.errors.length} error
                  {currentValidation.errors.length !== 1 ? "s" : ""} found:
                </h4>
                <div className="max-h-32 overflow-y-auto pr-2">
                  <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                    {currentValidation.errors.map((error, index) => (
                      <li key={index} className="leading-relaxed">
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Content - Scrollable; responsive padding for better use of space */}
        <div className="overflow-y-auto flex-1">
          <div className="px-6 sm:px-8 lg:px-10 xl:px-12 py-8">
            {getStepComponent(currentStep)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderStep()}
      <CloneProjectModal
        visible={showCloneModal}
        onClose={() => setShowCloneModal(false)}
      />
    </>
  );
};

export default WizardSteps;
